/**
 * Conversation history sidebar component
 * Shows list of past conversations with search and actions
 */

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  MessageSquare,
  Search,
  Trash2,
  Download,
  Plus,
  Calendar,
  Edit2,
  Folder,
  FolderOpen,
  ChevronDown,
  ChevronRight,
  MoreVertical,
  FolderPlus,
  Palette,
  RotateCcw,
  XCircle,
} from 'lucide-react';
import { useStorage } from '@/context/StorageContext';
import { useAgent } from '@/context/AgentContext';
import { usePreferences } from '@/context/PreferencesContext';
import { useAuth } from '@/context/AuthContext';
import {
  useConversations,
  useFolders,
  useTrash,
  CONVERSATIONS_QUERY_KEY,
  FOLDERS_QUERY_KEY,
  TRASH_QUERY_KEY,
} from '@/hooks/useConversations';
import type { Conversation } from '@/types/chat.types';
import type { Folder as FolderType } from '@/types/storage.types';
import { cn } from '@/utils/classNames';
import { formatDate } from '@/utils/formatters';
import { reportError } from '@/services/errorTelemetry';
import {
  findOrphanedConversationIds,
  evaluateAddToFolder,
} from '@/utils/folderHygiene';
import { describeBackendError } from '@/utils/backendErrorMessage';

interface ConversationHistoryProps {
  currentConversationId?: string;
  onSelectConversation: (conversation: Conversation) => void;
  onNewConversation: () => void;
  onDeleteConversation: (id: string) => void;
  className?: string;
}

export function ConversationHistory({
  currentConversationId,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
  className = '',
}: ConversationHistoryProps) {
  const { adapter } = useStorage();
  const { selectedAgent } = useAgent();
  const { preferences, updatePreferences } = usePreferences();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Conversations and folders are owned by React Query — App.tsx and this
  // component subscribe to the same useConversations()/useFolders() queries
  // (replaces the old refreshTrigger-coordinated parallel state). After
  // any mutation we call queryClient.invalidateQueries to refresh both.
  const conversationsQuery = useConversations();
  const foldersQuery = useFolders();
  const trashQuery = useTrash();
  // Stable references so downstream useMemo deps don't churn each render.
  const allConversations = useMemo(
    () => conversationsQuery.data ?? [],
    [conversationsQuery.data],
  );
  const folders = useMemo(
    () => foldersQuery.data ?? [],
    [foldersQuery.data],
  );
  const allTrash = useMemo(
    () => trashQuery.data ?? [],
    [trashQuery.data],
  );

  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set()
  );
  const [folderMenuId, setFolderMenuId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Conversation[] | null>(
    null,
  );
  const [isSearching, setIsSearching] = useState(false);
  const [trashExpanded, setTrashExpanded] = useState(false);
  const isLoading = conversationsQuery.isLoading;

  // Apply agent filter to trash items too.
  const trash = useMemo(() => {
    if (preferences.conversationFilter === 'current-agent' && selectedAgent) {
      return allTrash.filter((item) => item.agentId === selectedAgent.id);
    }
    return allTrash;
  }, [allTrash, preferences.conversationFilter, selectedAgent]);

  // Apply the agent filter from preferences. Search results, when present,
  // win over the live query.
  const conversations = useMemo<Conversation[]>(() => {
    const source = searchResults ?? allConversations;
    if (
      preferences.conversationFilter === 'current-agent' &&
      selectedAgent
    ) {
      return source.filter((conv) => conv.agentId === selectedAgent.id);
    }
    return source;
  }, [searchResults, allConversations, preferences.conversationFilter, selectedAgent]);

  // Pivot conversations into folder buckets + uncategorized list once
  // per data change. Source of truth is `conv.folderId` (mirrors the
  // `conversations.folder_id` column the write path updates); the
  // `folder.conversationIds` JSONB on the folder row is not kept in
  // sync by addConversationToFolder and must not be used for bucketing.
  const { byFolder, uncategorized } = useMemo(() => {
    const map = new Map<string, Conversation[]>();
    for (const folder of folders) {
      map.set(folder.id, []);
    }
    const uncat: Conversation[] = [];
    for (const conv of conversations) {
      const bucket = conv.folderId ? map.get(conv.folderId) : undefined;
      if (bucket) {
        bucket.push(conv);
      } else {
        uncat.push(conv);
      }
    }
    return { byFolder: map, uncategorized: uncat };
  }, [conversations, folders]);

  const groupedUncategorized = useMemo(
    () => groupByDate(uncategorized),
    [uncategorized],
  );

  // Debounced search. Empty query clears the override so the regular
  // query data shows again.
  useEffect(() => {
    if (!user) return;
    if (!searchQuery.trim()) {
      setIsSearching(false);
      setSearchResults(null);
      return;
    }
    const timeoutId = setTimeout(async () => {
      try {
        setIsSearching(true);
        const results = await adapter.searchConversations(searchQuery);
        const currentUserId = user.id || 'local-user';
        setSearchResults(results.filter((c) => c.userId === currentUserId));
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, adapter, user]);

  // Helper used by every mutation handler below to refresh both
  // conversations, folders, and trash queries in one call.
  const invalidateLists = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: TRASH_QUERY_KEY });
  }, [queryClient]);

  // Self-heal conversations tagged with a folder that no longer exists (e.g.
  // the folder's creation failed after the move, or it was deleted elsewhere).
  // They render in "Uncategorized" but keep a dead folder_id; clear it so the
  // data matches what the user sees.
  //
  // CRITICAL gate: both queries use `placeholderData: []`, so before their
  // first fetch resolves `folders` is an empty array and EVERY filed
  // conversation would look orphaned. Only act on real, settled server data
  // (`isSuccess && !isPlaceholderData`). `healedOrphansRef` stops a failed
  // clear from being retried every render.
  const healedOrphansRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (
      !foldersQuery.isSuccess ||
      foldersQuery.isPlaceholderData ||
      !conversationsQuery.isSuccess ||
      conversationsQuery.isPlaceholderData
    ) {
      return;
    }
    const orphanIds = findOrphanedConversationIds(
      allConversations,
      folders
    ).filter((id) => !healedOrphansRef.current.has(id));
    if (orphanIds.length === 0) return;
    orphanIds.forEach((id) => healedOrphansRef.current.add(id));
    void Promise.allSettled(
      // _folderId is ignored by the adapter — it PUTs folder_id: null.
      orphanIds.map((id) => adapter.removeConversationFromFolder(id, ''))
    ).then((results) => {
      if (results.some((r) => r.status === 'fulfilled')) {
        queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY });
      }
    });
  }, [
    allConversations,
    folders,
    foldersQuery.isSuccess,
    foldersQuery.isPlaceholderData,
    conversationsQuery.isSuccess,
    conversationsQuery.isPlaceholderData,
    adapter,
    queryClient,
  ]);

  const handleRename = useCallback(
    async (
      e: React.MouseEvent,
      conversationId: string,
      currentTitle: string
    ) => {
      e.stopPropagation();

      const newTitle = prompt('Enter new conversation title:', currentTitle);
      if (!newTitle || newTitle === currentTitle) {
        return;
      }

      try {
        await adapter.updateConversation(conversationId, { title: newTitle });
        invalidateLists();
      } catch (error) {
        reportError(error, { area: 'conversation-history', operation: 'rename', conversationId }, 'error');
        console.error('Failed to rename conversation:', error);
        alert('Failed to rename conversation');
      }
    },
    [adapter, invalidateLists]
  );

  const handleDelete = useCallback(
    async (e: React.MouseEvent, conversationId: string) => {
      e.stopPropagation();

      // No confirm() — trash IS the undo mechanism.
      try {
        await adapter.deleteConversation(conversationId);
        invalidateLists();
        onDeleteConversation(conversationId);
      } catch (error) {
        reportError(error, { area: 'conversation-history', operation: 'softDelete', conversationId }, 'error');
        console.error('Failed to soft-delete conversation:', error);
        alert('Failed to delete conversation');
      }
    },
    [adapter, invalidateLists, onDeleteConversation]
  );

  const handleRestore = useCallback(async (id: string) => {
    try {
      await adapter.restoreConversation(id);
      invalidateLists();
    } catch (error) {
      reportError(error, { area: 'conversation-history', operation: 'restore', conversationId: id }, 'error');
      console.error('Failed to restore conversation:', error);
      alert('Failed to restore conversation');
    }
  }, [adapter, invalidateLists]);

  const handlePurge = useCallback(async (id: string) => {
    if (!confirm('Permanently delete this conversation? This cannot be undone.')) return;
    try {
      await adapter.purgeConversation(id);
      invalidateLists();
    } catch (error) {
      reportError(error, { area: 'conversation-history', operation: 'purge', conversationId: id }, 'error');
      console.error('Failed to purge conversation:', error);
      alert('Failed to delete conversation');
    }
  }, [adapter, invalidateLists]);

  const handleEmptyTrash = useCallback(async () => {
    if (trash.length === 0) return;
    if (!confirm(`Permanently delete all ${trash.length} items in trash? This cannot be undone.`)) return;
    await Promise.all(trash.map((c) => adapter.purgeConversation(c.id).catch(() => {})));
    invalidateLists();
  }, [adapter, trash, invalidateLists]);

  const handleExport = useCallback(
    async (e: React.MouseEvent, conversation: Conversation) => {
      e.stopPropagation();

      try {
        // The sidebar's Conversation comes from the LIST endpoint, which
        // deliberately omits the `messages` JSONB for perf (commit
        // 02cbe97). `deserializeConversation` defaults the missing field
        // to `[]`, so serializing the sidebar object as-is produced an
        // export with `messages: []` even when the DB had the full
        // transcript — a user reported "the chat is gone" after looking
        // at the exported file. Fetch the detail record first so the
        // export contains the real messages array.
        const full = await adapter.loadConversation(conversation.id);
        if (!full) {
          reportError('Export: loadConversation returned null', {
            area: 'conversation-history',
            operation: 'export',
            conversationId: conversation.id,
          }, 'error');
          alert(
            'Could not load the conversation to export. It may have been deleted ' +
            'or your session expired.'
          );
          return;
        }
        const data = JSON.stringify(full, null, 2);
        const blob = new Blob([data], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `conversation-${full.id}.json`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        reportError(error, { area: 'conversation-history', operation: 'export', conversationId: conversation.id }, 'error');
        console.error('Failed to export conversation:', error);
        alert('Failed to export conversation');
      }
    },
    [adapter]
  );

  // Folder management functions
  const handleCreateFolder = async () => {
    const name = prompt('Enter folder name:');
    if (!name) return;

    try {
      const folder: FolderType = {
        id: `folder-${Date.now()}`,
        name,
        createdAt: new Date(),
        updatedAt: new Date(),
        conversationIds: [],
        userId: user?.id || 'local-user',
      };
      await adapter.createFolder(folder);
      invalidateLists();
    } catch (error) {
      reportError(error, { area: 'conversation-history', operation: 'createFolder' }, 'error');
      console.error('Failed to create folder:', error);
      // Tell the user whether it's worth retrying (server busy / offline) vs a
      // real problem (session expired / duplicate), instead of a flat
      // "Failed to create folder". We don't auto-retry: folder ids are
      // client-generated, so a retry of a request that actually succeeded
      // server-side would 409 on the duplicate id and confuse the message.
      alert(describeBackendError(error, 'create the folder').message);
    }
  };

  const handleRenameFolder = async (folderId: string, currentName: string) => {
    const newName = prompt('Enter new folder name:', currentName);
    if (!newName || newName === currentName) return;

    try {
      await adapter.updateFolder(folderId, { name: newName });
      invalidateLists();
      setFolderMenuId(null);
    } catch (error) {
      reportError(error, { area: 'conversation-history', operation: 'renameFolder', folderId }, 'error');
      console.error('Failed to rename folder:', error);
      alert('Failed to rename folder');
    }
  };

  const handleChangeFolderColor = async (folderId: string) => {
    const colors = [
      '#0052CC', '#6554C0', '#00875A', '#FF991F', '#DE350B',
      '#0065FF', '#00B8D9', '#36B37E', '#FFAB00', '#FF5630',
    ];
    const color = colors[Math.floor(Math.random() * colors.length)];

    try {
      await adapter.updateFolder(folderId, { color });
      invalidateLists();
      setFolderMenuId(null);
    } catch (error) {
      reportError(error, { area: 'conversation-history', operation: 'changeFolderColor', folderId }, 'error');
      console.error('Failed to change folder color:', error);
      alert('Failed to change folder color');
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    if (!confirm('Delete this folder? Conversations will not be deleted.')) return;

    try {
      await adapter.deleteFolder(folderId);
      invalidateLists();
      setFolderMenuId(null);
    } catch (error) {
      reportError(error, { area: 'conversation-history', operation: 'deleteFolder', folderId }, 'error');
      console.error('Failed to delete folder:', error);
      alert('Failed to delete folder');
    }
  };

  const handleAddToFolder = useCallback(
    async (conversationId: string, folderId: string) => {
      // Block moves that would orphan the conversation (folder gone) or
      // silently no-op (conversation not yet persisted). See evaluateAddToFolder.
      const check = evaluateAddToFolder(
        conversationId,
        folderId,
        allConversations,
        folders
      );
      if (!check.ok) {
        alert(check.message);
        return;
      }
      try {
        await adapter.addConversationToFolder(conversationId, folderId);
        invalidateLists();
      } catch (error) {
        reportError(error, { area: 'conversation-history', operation: 'addToFolder', conversationId, folderId }, 'error');
        console.error('Failed to add conversation to folder:', error);
        alert(describeBackendError(error, 'move the conversation').message);
      }
    },
    [adapter, invalidateLists, folders, allConversations]
  );

  const handleRemoveFromFolder = useCallback(
    async (conversationId: string, folderId: string) => {
      try {
        await adapter.removeConversationFromFolder(conversationId, folderId);
        invalidateLists();
      } catch (error) {
        reportError(error, { area: 'conversation-history', operation: 'removeFromFolder', conversationId, folderId }, 'error');
        console.error('Failed to remove conversation from folder:', error);
        alert('Failed to remove conversation from folder');
      }
    },
    [adapter, invalidateLists]
  );

  const toggleFolder = (folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  return (
    <div className={cn('flex h-full flex-col bg-bg-primary', className)}>
      {/* Header */}
      <div className="border-b border-border-default p-4">
        <button
          onClick={onNewConversation}
          className="mb-4 flex w-full items-center justify-center
          gap-2 rounded-lg bg-stellar-primary px-4 py-2.5
          text-white transition-colors hover:bg-stellar-primary-dark
          focus:outline-none focus:ring-2 focus:ring-stellar-primary
          focus:ring-offset-2"
        >
          <Plus className="h-5 w-5" />
          <span className="font-semibold">New Conversation</span>
        </button>

        {/* Search */}
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2
            text-text-tertiary"
          />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-border-default py-2
            pl-10 pr-4 text-sm bg-bg-primary text-text-primary
            placeholder:text-text-tertiary
            focus:border-stellar-primary focus:outline-none
            focus:ring-1 focus:ring-stellar-primary"
          />
          {isSearching && (
            <div
              className="absolute right-3 top-1/2 h-4 w-4
              -translate-y-1/2 animate-spin rounded-full border-2
              border-stellar-primary border-t-transparent"
            />
          )}
        </div>

        {/* Agent filter */}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs font-medium text-text-secondary">Show:</span>
          <div className="flex gap-1 rounded-lg bg-bg-secondary p-1">
            <button
              onClick={() =>
                updatePreferences({ conversationFilter: 'current-agent' })
              }
              className={cn(
                'rounded px-3 py-1 text-xs font-medium transition-colors',
                preferences.conversationFilter === 'current-agent'
                  ? 'bg-bg-primary text-stellar-primary shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              )}
            >
              Current Agent
            </button>
            <button
              onClick={() =>
                updatePreferences({ conversationFilter: 'all-agents' })
              }
              className={cn(
                'rounded px-3 py-1 text-xs font-medium transition-colors',
                preferences.conversationFilter === 'all-agents'
                  ? 'bg-bg-primary text-stellar-primary shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              )}
            >
              All Agents
            </button>
          </div>
        </div>
      </div>

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <div
              className="h-8 w-8 animate-spin rounded-full border-4
              border-stellar-primary border-t-transparent"
            />
          </div>
        ) : conversations.length === 0 && folders.length === 0 ? (
          <div className="p-8 text-center">
            <MessageSquare
              className="mx-auto mb-3 h-12 w-12 text-text-disabled"
            />
            <p className="text-sm text-text-secondary">
              {searchQuery
                ? 'No conversations found'
                : 'No conversations yet'}
            </p>
            <p className="mt-1 text-xs text-text-tertiary">
              Start a new conversation to get started
            </p>
          </div>
        ) : (
          <>
            {/* Folder button */}
            <div className="border-b border-border-default px-4 py-2">
              <button
                onClick={handleCreateFolder}
                className="flex w-full items-center gap-2 rounded-lg border border-border-default
                bg-bg-primary px-3 py-2 text-sm text-text-secondary transition-colors
                hover:bg-bg-secondary hover:text-text-primary"
              >
                <FolderPlus className="h-4 w-4" />
                <span>New Folder</span>
              </button>
            </div>

            {/* Folders */}
            {folders.map((folder) => {
              const folderConversations = byFolder.get(folder.id) ?? [];
              const isExpanded = expandedFolders.has(folder.id);

              return (
                <div key={folder.id} className="border-b border-border-default">
                  {/* Folder header */}
                  <button
                    onClick={() => toggleFolder(folder.id)}
                    className="flex w-full items-center gap-2 px-4 py-3 text-left
                    transition-colors hover:bg-bg-secondary"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-text-tertiary" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-text-tertiary" />
                    )}
                    {isExpanded ? (
                      <FolderOpen
                        className="h-5 w-5"
                        style={{ color: folder.color || '#6554C0' }}
                      />
                    ) : (
                      <Folder
                        className="h-5 w-5"
                        style={{ color: folder.color || '#6554C0' }}
                      />
                    )}
                    <span className="flex-1 text-sm font-semibold text-text-primary">
                      {folder.name}
                    </span>
                    <span className="text-xs text-text-tertiary">
                      {folderConversations.length}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const newId =
                          folderMenuId === folder.id ? null : folder.id;
                        setFolderMenuId(newId);
                      }}
                      className="rounded p-1 hover:bg-bg-tertiary"
                    >
                      <MoreVertical className="h-4 w-4 text-text-tertiary" />
                    </button>
                  </button>

                  {/* Folder menu */}
                  {folderMenuId === folder.id && (
                    <div
                      className="absolute right-4 z-20 mt-1 rounded-lg border
                      border-border-default bg-bg-primary shadow-lg"
                    >
                      <button
                        onClick={() =>
                          handleRenameFolder(folder.id, folder.name)
                        }
                        className="flex w-full items-center gap-2 px-4 py-2
                        text-sm text-text-primary hover:bg-bg-secondary"
                      >
                        <Edit2 className="h-4 w-4" />
                        Rename
                      </button>
                      <button
                        onClick={() => handleChangeFolderColor(folder.id)}
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm
                        text-text-primary hover:bg-bg-secondary"
                      >
                        <Palette className="h-4 w-4" />
                        Change Color
                      </button>
                      <button
                        onClick={() => handleDeleteFolder(folder.id)}
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm
                        text-error hover:bg-bg-secondary"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete Folder
                      </button>
                    </div>
                  )}

                  {/* Folder conversations */}
                  {isExpanded && folderConversations.map((conversation) => (
                    <div key={conversation.id} className="relative">
                      <ConversationItemRow
                        conversation={conversation}
                        isActive={conversation.id === currentConversationId}
                        folders={folders}
                        inFolderId={folder.id}
                        onSelectConversation={onSelectConversation}
                        onRename={handleRename}
                        onDelete={handleDelete}
                        onExport={handleExport}
                        onAddToFolder={handleAddToFolder}
                        onRemoveFromFolder={handleRemoveFromFolder}
                      />
                    </div>
                  ))}
                </div>
              );
            })}

            {/* Uncategorized conversations */}
            {(() => {
              if (uncategorized.length === 0) return null;

              return Object.entries(groupedUncategorized).map(
                ([date, convs]) => (
                  <div key={date} className="mb-4">
                  <div
                    className="sticky top-0 z-10 flex items-center gap-2
                    bg-bg-secondary px-4 py-2 text-xs font-semibold
                    text-text-secondary"
                  >
                    <Calendar className="h-3.5 w-3.5" />
                    {date}
                  </div>
                  {convs.map((conversation) => (
                    <ConversationItemRow
                      key={conversation.id}
                      conversation={conversation}
                      isActive={conversation.id === currentConversationId}
                      folders={folders}
                      onSelectConversation={onSelectConversation}
                      onRename={handleRename}
                      onDelete={handleDelete}
                      onExport={handleExport}
                      onAddToFolder={handleAddToFolder}
                      onRemoveFromFolder={handleRemoveFromFolder}
                    />
                  ))}
                </div>
              ));
            })()}
          </>
        )}

      </div>

      {/* Recently deleted (trash bin) — pinned footer so it stays reachable
          without scrolling past the whole active list. Always visible when
          non-empty; the item list scrolls independently when expanded. */}
      {trash.length > 0 && (
        <div className="border-t border-border-default">
          {/* Trash section header */}
          <div className="flex items-center gap-2 px-4 py-3">
            <button
              onClick={() => setTrashExpanded((v) => !v)}
              className="flex flex-1 items-center gap-2 text-left
              transition-colors hover:text-text-primary"
              aria-label={trashExpanded ? 'Collapse recently deleted' : 'Expand recently deleted'}
            >
              {trashExpanded ? (
                <ChevronDown className="h-4 w-4 text-text-tertiary" />
              ) : (
                <ChevronRight className="h-4 w-4 text-text-tertiary" />
              )}
              <Trash2 className="h-4 w-4 text-text-tertiary" />
              <span className="text-xs font-semibold text-text-secondary">
                Recently deleted ({trash.length})
              </span>
            </button>
            <button
              onClick={handleEmptyTrash}
              className="rounded px-2 py-1 text-xs text-error
              hover:bg-bg-secondary"
              aria-label="Empty trash"
              title="Empty trash"
            >
              Empty trash
            </button>
          </div>

          {/* Trash items — capped height + own scroll so an expanded trash
              never crowds out the active conversation list above. Actions
              are inline icon buttons (not a popup) so they're never clipped
              by this overflow container — matching the conversation rows. */}
          {trashExpanded && (
            <div className="max-h-64 overflow-y-auto">
              {trash.map((item) => {
                const daysLeft = Math.max(
                  0,
                  Math.floor(
                    (new Date(item.purgesAt).getTime() - Date.now()) /
                      86_400_000
                  )
                );

                return (
                  <div
                    key={item.id}
                    className="flex items-start justify-between gap-2
                    border-b border-border-default px-4 py-3
                    hover:bg-bg-secondary"
                  >
                    <div className="min-w-0 flex-1">
                      <h4 className="truncate text-sm font-semibold text-text-secondary">
                        {item.title}
                      </h4>
                      <p className="mt-0.5 text-xs text-text-tertiary">
                        Purges in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRestore(item.id);
                        }}
                        className="rounded p-1 text-text-tertiary
                        hover:bg-bg-tertiary hover:text-text-primary"
                        aria-label={`Restore ${item.title}`}
                        title="Restore"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePurge(item.id);
                        }}
                        className="rounded p-1 text-text-tertiary
                        hover:bg-bg-tertiary hover:text-error"
                        aria-label={`Permanently delete ${item.title}`}
                        title="Delete permanently"
                      >
                        <XCircle className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface ConversationItemProps {
  conversation: Conversation;
  isActive: boolean;
  onSelect: () => void;
  onRename: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  onExport: (e: React.MouseEvent) => void;
  folders?: FolderType[];
  onAddToFolder?: (folderId: string) => void;
  onRemoveFromFolder?: () => void;
}

function ConversationItemImpl({
  conversation,
  isActive,
  onSelect,
  onRename,
  onDelete,
  onExport,
  folders = [],
  onAddToFolder,
  onRemoveFromFolder,
}: ConversationItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [showFolderMenu, setShowFolderMenu] = useState(false);
  // The add-to-folder menu is rendered in a portal at fixed coordinates
  // computed from the trigger. This frees it from (a) the row's `isHovered`
  // block — so moving the cursor toward it doesn't unmount it — and (b) the
  // sidebar's overflow container, which would otherwise clip it.
  const [folderMenuPos, setFolderMenuPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const folderMenuRef = useRef<HTMLDivElement>(null);
  const folderBtnRef = useRef<HTMLButtonElement>(null);

  const MENU_WIDTH = 192; // w-48
  const openFolderMenu = useCallback(() => {
    const rect = folderBtnRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Right-align the menu to the trigger; flip above when there isn't
    // room below. Clamp to the viewport so it never renders off-screen.
    const estHeight = Math.min(folders.length * 40 + 8, 200);
    const openUp = rect.bottom + estHeight > window.innerHeight;
    const left = Math.max(8, rect.right - MENU_WIDTH);
    const top = openUp ? rect.top - estHeight - 4 : rect.bottom + 4;
    setFolderMenuPos({ top, left });
    setShowFolderMenu(true);
  }, [folders.length]);

  // Close the folder menu on any outside pointer-down or on scroll/resize
  // (a scrolled list would otherwise leave the fixed menu stranded).
  useEffect(() => {
    if (!showFolderMenu) return undefined;
    const onPointerDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        folderMenuRef.current?.contains(t) ||
        folderBtnRef.current?.contains(t)
      ) {
        return;
      }
      setShowFolderMenu(false);
    };
    const onDismiss = () => setShowFolderMenu(false);
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('scroll', onDismiss, true);
    window.addEventListener('resize', onDismiss);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('scroll', onDismiss, true);
      window.removeEventListener('resize', onDismiss);
    };
  }, [showFolderMenu]);

  const messageCount = conversation.messageCount;

  return (
    <button
      onClick={onSelect}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        'group relative w-full border-b border-border-default px-4 py-3',
        'text-left transition-colors hover:bg-bg-secondary',
        isActive && 'bg-bg-secondary border-l-4 border-l-stellar-primary'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h4
            className={cn(
              'truncate text-sm font-semibold text-text-primary'
            )}
          >
            {conversation.title}
          </h4>
          <div className="mt-1.5 flex items-center gap-3 text-xs text-text-tertiary">
            <span>{messageCount} messages</span>
            <span>{formatDate(conversation.updatedAt)}</span>
          </div>
        </div>

        {isHovered && (
          <div className="flex items-center gap-1">
            {onRemoveFromFolder && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveFromFolder();
                }}
                className="rounded p-1 text-text-tertiary hover:bg-bg-tertiary
                hover:text-text-primary"
                title="Remove from folder"
              >
                <FolderOpen className="h-4 w-4" />
              </button>
            )}
            {onAddToFolder && folders.length > 0 && (
              <button
                ref={folderBtnRef}
                onClick={(e) => {
                  e.stopPropagation();
                  if (showFolderMenu) {
                    setShowFolderMenu(false);
                  } else {
                    openFolderMenu();
                  }
                }}
                className="rounded p-1 text-text-tertiary hover:bg-bg-tertiary
                hover:text-text-primary"
                title="Add to folder"
              >
                <Folder className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={onRename}
              className="rounded p-1 text-text-tertiary hover:bg-bg-tertiary
              hover:text-text-primary"
              title="Rename conversation"
            >
              <Edit2 className="h-4 w-4" />
            </button>
            <button
              onClick={onExport}
              className="rounded p-1 text-text-tertiary hover:bg-bg-tertiary
              hover:text-text-primary"
              title="Export conversation"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={onDelete}
              className="rounded p-1 text-text-tertiary hover:bg-error-light
              hover:text-error dark:hover:bg-error-dark"
              title="Delete conversation"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Add-to-folder menu — portaled to <body> at fixed coords so it
          escapes the sidebar's overflow clipping and stays mounted while
          the cursor moves toward it (independent of the row's hover). */}
      {showFolderMenu &&
        folderMenuPos &&
        createPortal(
          <div
            ref={folderMenuRef}
            role="menu"
            aria-label="Add to folder"
            className="fixed z-50 w-48 rounded-lg border border-border-default
            bg-bg-primary shadow-lg"
            style={{ top: folderMenuPos.top, left: folderMenuPos.left }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-h-48 overflow-y-auto">
              {folders.map((folder) => (
                <button
                  key={folder.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddToFolder?.(folder.id);
                    setShowFolderMenu(false);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm
                  text-text-primary hover:bg-bg-secondary"
                >
                  <Folder
                    className="h-4 w-4"
                    style={{ color: folder.color || '#6554C0' }}
                  />
                  <span className="truncate">{folder.name}</span>
                </button>
              ))}
            </div>
          </div>,
          document.body
        )}
    </button>
  );
}

function areConversationItemPropsEqual(
  prev: Readonly<ConversationItemProps>,
  next: Readonly<ConversationItemProps>
): boolean {
  // `conversation` is a fresh object on every cache update — compare its
  // load-bearing scalars rather than the reference. The messages.length
  // check preserves the "message count" badge without a deep-array
  // compare. (F-6)
  return (
    prev.conversation.id === next.conversation.id &&
    prev.conversation.title === next.conversation.title &&
    prev.conversation.updatedAt === next.conversation.updatedAt &&
    prev.conversation.messageCount === next.conversation.messageCount &&
    prev.isActive === next.isActive &&
    prev.folders === next.folders &&
    prev.onSelect === next.onSelect &&
    prev.onRename === next.onRename &&
    prev.onDelete === next.onDelete &&
    prev.onExport === next.onExport &&
    prev.onAddToFolder === next.onAddToFolder &&
    prev.onRemoveFromFolder === next.onRemoveFromFolder
  );
}

const ConversationItem = React.memo(
  ConversationItemImpl,
  areConversationItemPropsEqual
);

// F-6: per-row wrapper that owns its own stable `useCallback`s keyed on
// `conversation.id`. Without this, the per-row arrow wrappers built
// inline in the JSX `map` body would create fresh function identities
// on every parent render (e.g. every search-input keystroke), bailing
// the ConversationItem memo comparator.
interface ConversationItemRowProps {
  conversation: Conversation;
  isActive: boolean;
  folders: FolderType[];
  inFolderId?: string;
  onSelectConversation: (conversation: Conversation) => void;
  onRename: (
    e: React.MouseEvent,
    id: string,
    title: string
  ) => void;
  onDelete: (e: React.MouseEvent, id: string) => void;
  onExport: (e: React.MouseEvent, conversation: Conversation) => void;
  onAddToFolder: (conversationId: string, folderId: string) => void;
  onRemoveFromFolder: (
    conversationId: string,
    folderId: string
  ) => void;
}

const ConversationItemRow = React.memo(function ConversationItemRow({
  conversation,
  isActive,
  folders,
  inFolderId,
  onSelectConversation,
  onRename,
  onDelete,
  onExport,
  onAddToFolder,
  onRemoveFromFolder,
}: ConversationItemRowProps) {
  const handleSelect = useCallback(
    () => onSelectConversation(conversation),
    [conversation, onSelectConversation]
  );
  const handleRenameRow = useCallback(
    (e: React.MouseEvent) =>
      onRename(e, conversation.id, conversation.title),
    [conversation.id, conversation.title, onRename]
  );
  const handleDeleteRow = useCallback(
    (e: React.MouseEvent) => onDelete(e, conversation.id),
    [conversation.id, onDelete]
  );
  const handleExportRow = useCallback(
    (e: React.MouseEvent) => onExport(e, conversation),
    [conversation, onExport]
  );
  const handleAddToFolderRow = useCallback(
    (folderId: string) => onAddToFolder(conversation.id, folderId),
    [conversation.id, onAddToFolder]
  );
  const handleRemoveFromFolderRow = useCallback(() => {
    if (inFolderId) onRemoveFromFolder(conversation.id, inFolderId);
  }, [conversation.id, inFolderId, onRemoveFromFolder]);

  return (
    <ConversationItem
      conversation={conversation}
      isActive={isActive}
      onSelect={handleSelect}
      onRename={handleRenameRow}
      onDelete={handleDeleteRow}
      onExport={handleExportRow}
      folders={folders}
      onAddToFolder={handleAddToFolderRow}
      onRemoveFromFolder={inFolderId ? handleRemoveFromFolderRow : undefined}
    />
  );
});

function groupByDate(
  conversations: Conversation[]
): Record<string, Conversation[]> {
  const groups: Record<string, Conversation[]> = {};
  const today = new Date();
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

  for (const conv of conversations) {
    const date = new Date(conv.updatedAt);
    let label: string;

    if (isSameDay(date, today)) {
      label = 'Today';
    } else if (isSameDay(date, yesterday)) {
      label = 'Yesterday';
    } else if (date > weekAgo) {
      label = 'Last 7 days';
    } else {
      label = date.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      });
    }

    if (!groups[label]) {
      groups[label] = [];
    }
    groups[label].push(conv);
  }

  return groups;
}

function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}
